// src/layout/common/MasterDataDisplay.jsx
import React, { useEffect } from "react";
import useAuthStore from "../../store/useAuthStore";
import useProjectInfoStore from "../../store/builders/useProjectInfoStore"; // Builder store
import useCustomerProjectInfoStore from "../../store/customer/useProjectInfoStore"; // Customer store
import { ClipLoader } from "react-spinners";
import {
  BuildingOffice2Icon,
  UserCircleIcon,
  MapPinIcon,
  PhoneIcon,
  AtSymbolIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ArrowUpRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  ChatBubbleBottomCenterTextIcon,
  ArchiveBoxIcon, // For Invitation or other generic document-like items
} from "@heroicons/react/24/outline";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Helper to format date and time
const formatDateTime = (dateString, includeTime = false) => {
  if (!dateString) return "N/A";
  try {
    const options = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    if (includeTime) {
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.hour12 = true;
    }
    return new Date(dateString).toLocaleDateString("en-GB", options);
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return "Invalid Date";
  }
};

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatStatus = (status) => {
  if (!status) return "N/A";
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const InfoItem = ({
  icon: Icon,
  label,
  value,
  children,
  className = "",
  isMultiline = false,
  isHtml = false,
}) => (
  <div className={`flex items-start space-x-3 ${className}`}>
    {Icon && <Icon className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />}
    <div>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <div
        className={`text-sm text-gray-800 mt-0.5 ${
          isMultiline ? "whitespace-pre-wrap" : ""
        }`}
      >
        {isHtml && typeof value === "string" ? (
          <div dangerouslySetInnerHTML={{ __html: value }} />
        ) : value !== undefined ? (
          value
        ) : (
          children || "N/A"
        )}
      </div>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  return (
    <div className="bg-white shadow rounded-lg border border-gray-100 mb-6">
      <button
        className="w-full flex justify-between items-center p-4 sm:p-5 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
          {Icon && (
            <Icon className="w-5 h-5 mr-2.5 text-blue-600 flex-shrink-0" />
          )}
          {title}
        </h2>
        <ChevronIcon isOpen={isOpen} />
      </button>
      {isOpen && (
        <div className="p-4 sm:p-5 border-t border-gray-200">{children}</div>
      )}
    </div>
  );
};

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
      isOpen ? "rotate-180" : ""
    }`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 9l-7 7-7-7"
    ></path>
  </svg>
);

const DocumentLink = ({ url, label }) => {
  if (!url) {
    return <span className="text-gray-400 italic text-xs">Not Uploaded</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 hover:underline text-xs inline-flex items-center"
    >
      {label}
      <ArrowUpRightIcon className="w-3 h-3 ml-1" />
    </a>
  );
};

const MasterDataDisplay = ({ projectId }) => {
  const { userType } = useAuthStore();
  
  // Use the appropriate store based on user type
  const builderStore = useProjectInfoStore();
  const customerStore = useCustomerProjectInfoStore();
  
  // Select the store based on user type
  const store = userType === "customer" ? customerStore : builderStore;
  const { masterData, fetchProjectMasterData, isLoading, error } = store;
  
  const navigate = useNavigate();

  useEffect(() => {
    if (projectId) {
      fetchProjectMasterData(projectId);
    }
  }, [projectId, fetchProjectMasterData]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow">
        <ClipLoader size={40} color={"#3B82F6"} />
        <p className="ml-3 text-gray-600">Loading Project Data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-6 text-red-600 bg-red-50 rounded-lg shadow border border-red-200">
        <ExclamationTriangleIcon className="w-10 h-10 mx-auto mb-2 text-red-500" />
        <p className="font-semibold">Error loading project data:</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!masterData) {
    return (
      <div className="text-center p-6 text-gray-500 bg-white rounded-lg shadow">
        No project data available.
      </div>
    );
  }

  const { project, customer, builder, messages } = masterData;
  const { holds, payments, documents, invitation, enquiry, enquiryComments } =
    project || {};

  const handleGoBack = () => navigate(-1);

  return (
    <div className="space-y-6 p-1">
      <div className="flex items-center mb-4">
        <button
          onClick={handleGoBack}
          className="p-1.5 mr-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-full transition-colors duration-150"
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold text-gray-800">
          Project Master Details (ID: {projectId})
        </h1>
      </div>

      <Section
        title="Project Overview"
        icon={ClipboardDocumentListIcon}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoItem
            label="Project Name"
            value={project?.project_name}
            icon={ClipboardDocumentListIcon}
            className="lg:col-span-2"
          />
          <InfoItem label="Status" value={formatStatus(project?.status)} />
          <InfoItem
            label="Location"
            value={project?.project_location}
            icon={MapPinIcon}
            className="lg:col-span-2"
          />
          <InfoItem
            label="Budget"
            value={formatCurrency(project?.project_budget)}
            icon={CurrencyRupeeIcon}
          />
          <InfoItem
            label="Start Date"
            value={formatDateTime(project?.starting_date)}
            icon={CalendarDaysIcon}
          />
          <InfoItem
            label="Deadline"
            value={formatDateTime(project?.deadline_date)}
            icon={CalendarDaysIcon}
          />
          <InfoItem
            label="Completed Date"
            value={formatDateTime(project?.completed_date)}
            icon={CalendarDaysIcon}
          />
          <InfoItem
            label="Created On"
            value={formatDateTime(project?.created_at, true)}
            icon={ClockIcon}
          />
          <InfoItem
            label="Last Updated"
            value={formatDateTime(project?.updated_at, true)}
            icon={ClockIcon}
          />
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Builder Information" icon={BuildingOffice2Icon}>
          <div className="space-y-3">
            <InfoItem label="Company" value={builder?.company} />
            <InfoItem label="Engineer" value={builder?.name} />
            <InfoItem
              label="Ducktail ID"
              value={builder?.builder_id}
              icon={IdentificationIcon}
            />
            <InfoItem
              label="Contact"
              value={builder?.contact ? `+91 ${builder.contact}` : "N/A"}
              icon={PhoneIcon}
            />
            <InfoItem
              label="Email"
              value={builder?.email}
              icon={AtSymbolIcon}
            />
            {builder?.image && (
              <InfoItem label="Profile Image">
                <a
                  href={builder.image}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={builder.image}
                    alt="Builder Profile"
                    className="w-16 h-16 rounded-md object-cover mt-1 border hover:shadow-md transition-shadow"
                  />
                </a>
              </InfoItem>
            )}
          </div>
        </Section>

        <Section title="Customer Information" icon={UserCircleIcon}>
          <div className="space-y-3">
            <InfoItem label="Name" value={customer?.name} />
            <InfoItem
              label="Ducktail ID"
              value={customer?.customer_id}
              icon={IdentificationIcon}
            />
            <InfoItem
              label="Email"
              value={customer?.email}
              icon={AtSymbolIcon}
            />
            <InfoItem label="Address" icon={MapPinIcon} isHtml={true}>
              {customer?.address ? (
                <>
                  {customer.address.address_line1}
                  {customer.address.address_line2 &&
                    `, ${customer.address.address_line2}`}
                  <br />
                  {customer.address.taluk}, {customer.address.district}
                  <br />
                  {customer.address.state} - {customer.address.postcode}
                  {customer.address.latitude && customer.address.longitude && (
                    <span className="text-xs block text-gray-400 mt-1">
                      (Lat: {customer.address.latitude}, Lng:{" "}
                      {customer.address.longitude})
                    </span>
                  )}
                </>
              ) : (
                "N/A"
              )}
            </InfoItem>
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {enquiry && (
          <Section
            title="Original Enquiry Details"
            icon={ClipboardDocumentListIcon}
          >
            <div className="space-y-3">
              <InfoItem label="Enquirer Name" value={enquiry.customer_name} />
              <InfoItem label="Phone" value={enquiry.phone_number ? `+91 ${enquiry.phone_number}` : "N/A"} />
              <InfoItem
                label="Location"
                value={`${enquiry.taluk}, ${enquiry.district}, ${enquiry.state} - ${enquiry.postcode}`}
                icon={MapPinIcon}
              />
              <InfoItem
                label="Primary Enquiry Comment"
                value={enquiry.comments}
                isMultiline={true}
              />
              <InfoItem label="Status" value={formatStatus(enquiry.status)} />
              <InfoItem
                label="Enquired On"
                value={formatDateTime(enquiry.created_at, true)}
                icon={ClockIcon}
              />
              <InfoItem
                label="Last Action On"
                value={formatDateTime(enquiry.updated_at, true)}
                icon={ClockIcon}
              />
            </div>
          </Section>
        )}

        {invitation && (
          <Section title="Project Invitation Details" icon={ArchiveBoxIcon}>
            <div className="space-y-3">
              <InfoItem
                label="Invitation Status"
                value={formatStatus(invitation.status)}
              />
              {/* <InfoItem
                label="Invited For Service"
                value={invitation.service || "General Construction"}
              /> */}
              <InfoItem
                label="Invited For Location"
                value={`${invitation.taluk}, ${invitation.district} - ${invitation.postal_code}`}
                icon={MapPinIcon}
              />
              <InfoItem
                label="Invited On"
                value={formatDateTime(invitation.created_at, true)}
                icon={ClockIcon}
              />
              <InfoItem
                label="Invitation Last Updated"
                value={formatDateTime(invitation.updated_at, true)}
                icon={ClockIcon}
              />
            </div>
          </Section>
        )}
      </div>

      {enquiryComments && enquiryComments.length > 0 && (
        <Section
          title="Enquiry Comment History"
          icon={ChatBubbleBottomCenterTextIcon}
        >
          <ul className="space-y-3 divide-y divide-gray-100 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
            {enquiryComments.map((comment) => (
              <li key={comment.id} className="pt-3 first:pt-0">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {comment.comment}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDateTime(comment.created_at, true)}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {documents && (
        <Section title="Completion Documents" icon={DocumentTextIcon}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <InfoItem label="Specification Report">
              <DocumentLink
                url={documents.specification_report_url}
                label="View/Download"
              />
            </InfoItem>
            <InfoItem label="Warranty Document">
              <DocumentLink
                url={documents.warranty_document_url}
                label="View/Download"
              />
            </InfoItem>
            <InfoItem label="Completion Certificate">
              <DocumentLink
                url={documents.completion_certificate_url}
                label="View/Download"
              />
            </InfoItem>
            <InfoItem label="Site Image">
              {documents.site_image_url ? (
                <a
                  href={documents.site_image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={documents.site_image_url}
                    alt="Site"
                    className="w-24 h-24 rounded-md object-cover mt-1 border hover:shadow-md transition-shadow"
                  />
                </a>
              ) : (
                <span className="text-gray-400 italic text-xs">
                  Not Uploaded
                </span>
              )}
            </InfoItem>
          </div>
        </Section>
      )}

      {holds && holds.length > 0 && (
        <Section title="Hold History" icon={ClockIcon}>
          <ul className="space-y-3 divide-y divide-gray-100 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
            {holds.map((hold) => (
              <li key={hold.id} className="pt-3 first:pt-0">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {hold.comment}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDateTime(hold.created_at, true)}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {payments && payments.length > 0 && (
        <Section title="Project Payments History" icon={CreditCardIcon}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="py-2 px-3 text-left font-medium">Date</th>
                  <th className="py-2 px-3 text-left font-medium">Amount</th>
                  <th className="py-2 px-3 text-left font-medium">Status</th>
                  <th className="py-2 px-3 text-left font-medium">
                    Comment (by Builder)
                  </th>
                  <th className="py-2 px-3 text-left font-medium">
                    Payment Method
                  </th>
                  <th className="py-2 px-3 text-left font-medium">Ref ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50/50">
                    <td className="py-2 px-3 whitespace-nowrap">
                      {formatDateTime(payment.transaction_date, true)}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          payment.transaction_status?.toLowerCase() === "paid"
                            ? "bg-green-100 text-green-700"
                            : payment.transaction_status?.toLowerCase() ===
                              "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {formatStatus(payment.transaction_status)}
                      </span>
                    </td>
                    <td
                      className="py-2 px-3 max-w-xs truncate"
                      title={payment.comment}
                    >
                      {payment.comment || "-"}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {payment.payment_method || "-"}
                    </td>
                    <td
                      className="py-2 px-3 whitespace-nowrap max-w-[100px] truncate"
                      title={payment.transaction_reference_id}
                    >
                      {payment.transaction_reference_id || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {messages && messages.length > 0 && (
        <Section title="Recent Chat Messages" icon={ChatBubbleLeftRightIcon}>
          <ul className="space-y-3 divide-y divide-gray-100 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
            {messages.map((msg) => (
              <li key={msg.id} className="pt-3 first:pt-0">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {msg.message}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="font-medium">
                    {msg.sender_id === builder?.id
                      ? "Builder"
                      : msg.sender_id === customer?.id
                      ? "Customer"
                      : "Unknown Sender"}
                  </span>{" "}
                  - {formatDateTime(msg.created_at, true)}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #aaa;
        }
      `}</style>
    </div>
  );
};

export default MasterDataDisplay;